import { useEffect, useState } from 'react';
import { useLocation, Routes, Route, Navigate } from 'react-router';
import { useSelector } from 'react-redux';
import { styled } from '@mui/system';
import { NavTabs } from '@components/NavTabs';
import { ControlledToaster } from '@components/Toaster';
import { ACCOUNT_SETTINGS_TABS } from '@constants/tabs';
import { useTranslation } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { RoutePath } from '@constants/routes';
import { childRoutePath } from '@helpers/navigation';
import RedirectKeepingSearch from '@features/RedirectKeepingSearch';
import { GeneralSettings } from './GeneralSettings';
import BillingSettings from './BillingSettings';
import UserManagement from './UserManagement';
import Integrations from './Integrations';
import Profile from './Profile';
import Channels from './Channels';
import Brands from './Brands';
import TeamManagement from './TeamManagement';
import PageContainer from '@components/Layout/PageContainer';

const PageWrapper = styled(PageContainer)({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
});

const AccountSettingsPage = () => {
    const { t } = useTranslation();
    const { pathname, search } = useLocation();

    const [availableTabs, setAvailableTabs] = useState([]);
    const [openAlert, setOpenAlert] = useState(false);
    const [currentTab, setCurrentTab] = useState(pathname);

    const account = useSelector(state => state.account?.account);
    const { hasSettingsAccess, hasFullSettings } = usePermissions();
    const productsMaxNumberExceeded =
        account?.companiesMaxCount < account?.companies?.length || account?.usersMaxCount < account?.users?.length;
    const billingExpired = account && account?.billingDiffInSeconds < 0;

    useEffect(() => {
        setCurrentTab(pathname);
    }, [pathname]);

    useEffect(() => {
        if (productsMaxNumberExceeded || billingExpired) {
            setOpenAlert(true);
        }
    }, [productsMaxNumberExceeded, billingExpired]);

    useEffect(() => {
        let translatedTabs = ACCOUNT_SETTINGS_TABS.map(tab => {
            const isDisabled =
                (tab.value === RoutePath.AccountSettingsIntegrations ||
                    tab.value === RoutePath.AccountSettingsChannels) &&
                (productsMaxNumberExceeded || billingExpired);
            return {
                ...tab,
                label: t(tab.label),
                disabledTooltip: isDisabled ? t('common.subscriptionExpiredMsg') : null,
                disabled: isDisabled,
            };
        });
        if (!hasFullSettings()) {
            translatedTabs = translatedTabs.filter(tab => {
                return (
                    tab.value !== RoutePath.AccountSettingsGeneral &&
                    tab.value !== RoutePath.AccountSettingsBilling &&
                    tab.value !== RoutePath.AccountSettingsIntegrations &&
                    tab.value !== RoutePath.AccountSettingsBrands &&
                    tab.value !== RoutePath.AccountSettingsChannels &&
                    tab.value !== RoutePath.AccountSettingsTeams
                );
            });
        }
        if (!hasSettingsAccess()) {
            translatedTabs = translatedTabs.filter(tab => tab.value === RoutePath.AccountSettingsProfile);
        }
        setAvailableTabs(translatedTabs);
    }, [hasFullSettings, hasSettingsAccess, account]);

    const getAvailableTabIndex = () => {
        const tabIndex = {};
        let index = 0;
        availableTabs.forEach(tab => {
            tabIndex[tab.value] = index++;
        });
        return tabIndex;
    };

    return (
        <PageWrapper>
            <NavTabs
                tabLinks={availableTabs}
                onTabClick={selectedTab => setCurrentTab(selectedTab)}
                currentTabIndex={getAvailableTabIndex()[currentTab]}
            />
            <Routes>
                <Route
                    path={childRoutePath(RoutePath.AccountSettingsProfile, RoutePath.AccountSettings)}
                    element={<Profile />}
                />
                <Route
                    path={childRoutePath(RoutePath.AccountSettingsGeneral, RoutePath.AccountSettings)}
                    element={
                        new URLSearchParams(search).get('sessionId') ? (
                            <RedirectKeepingSearch to={RoutePath.AccountSettingsBilling} />
                        ) : (
                            <GeneralSettings />
                        )
                    }
                />
                <Route
                    path={childRoutePath(RoutePath.AccountSettingsBilling, RoutePath.AccountSettings)}
                    element={<BillingSettings />}
                />
                <Route
                    path={childRoutePath(RoutePath.AccountSettingsBrands, RoutePath.AccountSettings)}
                    element={<Brands />}
                />
                <Route
                    path={childRoutePath(RoutePath.AccountSettingsChannels, RoutePath.AccountSettings)}
                    element={<Channels />}
                />
                <Route
                    path={childRoutePath(RoutePath.AccountSettingsUsers, RoutePath.AccountSettings)}
                    element={<UserManagement />}
                />
                <Route
                    path={childRoutePath(RoutePath.AccountSettingsTeams, RoutePath.AccountSettings)}
                    element={<TeamManagement />}
                />
                <Route
                    path={childRoutePath(RoutePath.AccountSettingsIntegrations, RoutePath.AccountSettings)}
                    element={<Integrations />}
                />
                <Route path="*" element={<Navigate to={RoutePath.AccountSettingsProfile} replace />} />
            </Routes>
            <ControlledToaster
                autoHideDuration={null}
                text={t('common.subscriptionExpiredMsg')}
                type="error"
                open={openAlert}
                setOpen={setOpenAlert}
            />
        </PageWrapper>
    );
};

export default AccountSettingsPage;
